import type { ILogRocket } from '@/services/Logger';
import { initLongTaskBuffer, getLongTaskCount as sharedGetLongTaskCount } from '@/utils/longTaskBuffer';
import { METRIC_EVENTS } from '@/utils/metricsConstants';

type Primitive = string | number | boolean | undefined;

type NetworkDiagnosticsOptions = {
  logger?: ILogRocket;
  enabled: boolean;
  sampleEveryMs?: number;
  probeTimeoutMs?: number;
  probeUrls?: string[];
  //  ms above which a probe is considered "slow" — network likely cause
  probeSlowThresholdMs?: number;
  //  ms above which median API time is considered "slow" — infrastructure likely cause
  apiSlowThresholdMs?: number;
  debug?: boolean;
};

type LikelyCause = 'network' | 'infrastructure' | 'client-code' | 'ambiguous';

type NetworkConnectionLike = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener?: (name: 'change', cb: () => void) => void;
  removeEventListener?: (name: 'change', cb: () => void) => void;
};

type NetworkDiagnosticsHandle = {
  stop: () => void;
  runNow: () => Promise<void>;
};

function toRounded(n: unknown): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : undefined;
}

function isPatched(fn: unknown): boolean | undefined {
  if (typeof fn !== 'function') return undefined;

  try {
    return !Function.prototype.toString.call(fn).includes('[native code]');
  }
  catch {
    return undefined;
  }
}

function getConnection(): NetworkConnectionLike | undefined {
  const nav = window.navigator as Navigator & { connection?: NetworkConnectionLike };
  return nav.connection;
}

function getDefaultProbeUrls(): string[] {
  try {
    return [new URL('/favicon.ico', window.location.origin).toString()];
  }
  catch {
    return ['/favicon.ico'];
  }
}

/**
 * Samples recent resource timing entries for XHR/fetch calls to the app's
 * own origin and returns a simple { p50Ms, p95Ms, count }.
 * Uses the last `maxEntries` entries to avoid scanning the entire buffer.
 */
function getRecentApiTiming(maxEntries = 50): { p50Ms: number | undefined; p95Ms: number | undefined; count: number } {
  if (typeof performance?.getEntriesByType !== 'function') {
    return { p50Ms: undefined, p95Ms: undefined, count: 0 };
  }

  try {
    const { origin } = window.location;
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const recent = entries.slice(-maxEntries);

    const durations = recent
      .filter((e) => {
        const initiator = (e as PerformanceResourceTiming & { initiatorType?: string }).initiatorType;
        return (initiator === 'fetch' || initiator === 'xmlhttprequest') && e.name.startsWith(origin);
      })
      .map((e) => e.duration)
      .filter((d) => d > 0);

    if (!durations.length) return { p50Ms: undefined, p95Ms: undefined, count: 0 };

    const sorted = durations.slice().sort((a, b) => a - b);
    const p50Ms = Math.round(sorted[Math.floor((sorted.length - 1) * 0.5)]);
    const p95Ms = Math.round(sorted[Math.floor((sorted.length - 1) * 0.95)]);

    return { p50Ms, p95Ms, count: durations.length };
  }
  catch {
    return { p50Ms: undefined, p95Ms: undefined, count: 0 };
  }
}

/**
 * Returns the number of longtask performance entries recorded in the last
 * `windowMs` milliseconds. A non-zero value alongside fast probes and fast
 * APIs points to client-side rendering/JS as the latency source.
 *
 * @deprecated Prefer using the longTaskObserver from createLongTaskObserver()
 * which ensures entries are actually buffered by the browser.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getRecentLongTaskCount(windowMs = 5 * 60 * 1000): number {
  if (typeof performance?.getEntriesByType !== 'function') return 0;

  try {
    const now = performance.now();
    const entries = performance.getEntriesByType('longtask') as PerformanceEntry[];
    return entries.filter((e) => e.startTime >= now - windowMs).length;
  }
  catch {
    return 0;
  }
}

/**
 * Applies the separation table to assign a likely latency cause bucket.
 *
 * | Probe fast? | API fast? | Long tasks? | Likely cause     |
 * |-------------|-----------|-------------|------------------|
 * | No          | —         | —           | network          |
 * | Yes         | No        | —           | infrastructure   |
 * | Yes         | Yes       | Yes         | client-code      |
 * | Yes         | Yes       | No          | ambiguous        |
 */
function computeLikelyCause(opts: {
  probeMs: number | undefined;
  apiP50Ms: number | undefined;
  longTaskCount: number;
  probeSlowThresholdMs: number;
  apiSlowThresholdMs: number;
}): LikelyCause {
  const {
    probeMs, apiP50Ms, longTaskCount, probeSlowThresholdMs, apiSlowThresholdMs,
  } = opts;

  if (probeMs !== undefined && probeMs >= probeSlowThresholdMs) return 'network';
  if (probeMs !== undefined && probeMs < probeSlowThresholdMs) {
    if (apiP50Ms !== undefined && apiP50Ms >= apiSlowThresholdMs) return 'infrastructure';
    if (apiP50Ms !== undefined && apiP50Ms < apiSlowThresholdMs && longTaskCount > 0) return 'client-code';
  }
  return 'ambiguous';
}

async function runProbe(url: string, timeoutMs: number): Promise<{ ok: boolean; ms?: number }> {
  const hasAbort = typeof AbortController !== 'undefined';
  const controller = hasAbort ? new AbortController() : undefined;
  const timeoutId = hasAbort
    ? window.setTimeout(() => controller?.abort(), timeoutMs)
    : undefined;

  const started = performance.now();

  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller?.signal,
    });

    return { ok: response.ok, ms: Math.round(performance.now() - started) };
  }
  catch {
    return { ok: false, ms: Math.round(performance.now() - started) };
  }
  finally {
    if (typeof timeoutId === 'number') {
      window.clearTimeout(timeoutId);
    }
  }
}

export function initNetworkDiagnostics(options: NetworkDiagnosticsOptions): NetworkDiagnosticsHandle {
  const {
    logger,
    enabled,
    sampleEveryMs = 5 * 60 * 1000,
    probeTimeoutMs = 3_000,
    probeUrls = getDefaultProbeUrls(),
    probeSlowThresholdMs = 800,
    apiSlowThresholdMs = 1_500,
    debug = false,
  } = options;

  if (!enabled) {
    return {
      stop: () => {},
      runNow: async () => {},
    };
  }

  const longTaskObserver = { getCount: (windowMs = 5 * 60 * 1000) => sharedGetLongTaskCount(windowMs) };

  //  Ensure the shared buffer is initialized
  initLongTaskBuffer();

  const emit = (reason: string, extra: Record<string, Primitive> = {}) => {
    const connection = getConnection();
    const xhrProto = (window.XMLHttpRequest as { prototype?: { open?: unknown } }).prototype;

    const raw: Record<string, Primitive> = {
      reason,
      online: window.navigator.onLine,
      connectionApiAvailable: !!connection,
      effectiveType: connection?.effectiveType || 'unknown',
      downlinkMbps: toRounded(connection?.downlink),
      rttMs: toRounded(connection?.rtt),
      saveData: connection?.saveData,
      hardwareConcurrency: window.navigator.hardwareConcurrency,
      deviceMemoryGb: toRounded((window.navigator as Navigator & { deviceMemory?: number }).deviceMemory),
      webdriver: !!window.navigator.webdriver,
      fetchPatched: isPatched(window.fetch),
      xhrPatched: isPatched(xhrProto?.open),
      websocketPatched: isPatched(window.WebSocket),
      ...extra,
    };

    //  Strip undefined values — LogRocket track() rejects them with console warnings
    const payload = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== undefined),
    );

    logger?.track(METRIC_EVENTS.NETWORK_DIAGNOSTICS, payload);

    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[network-diagnostics]', payload);
    }
  };

  const emitWithProbes = async (reason: string) => {
    const probeEntries = await Promise.all(
      probeUrls.slice(0, 2).map((url) => runProbe(url, probeTimeoutMs)),
    );

    const probe1Ms = probeEntries[0]?.ms;
    const { p50Ms: apiP50Ms, p95Ms: apiP95Ms, count: apiSampleCount } = getRecentApiTiming();
    const longTaskCount = longTaskObserver.getCount();
    const likelyCause = computeLikelyCause({
      probeMs: probe1Ms,
      apiP50Ms,
      longTaskCount,
      probeSlowThresholdMs,
      apiSlowThresholdMs,
    });

    emit(reason, {
      probeCount: probeEntries.length,
      probe1Ms,
      probe1Ok: probeEntries[0]?.ok,
      probe2Ms: probeEntries[1]?.ms,
      probe2Ok: probeEntries[1]?.ok,
      apiP50Ms,
      apiP95Ms,
      apiSampleCount,
      longTaskCount,
      likelyCause,
    });
  };

  const scheduleEmitWithProbes = (reason: string) => {
    emitWithProbes(reason).catch(() => {
      if (debug) {
        // eslint-disable-next-line no-console
        console.debug('[network-diagnostics] probe emit failed', { reason });
      }
    });
  };

  const onOnline = () => {
    scheduleEmitWithProbes('online');
  };

  const onOffline = () => {
    emit('offline');
  };

  const onConnectionChange = () => {
    scheduleEmitWithProbes('connection-change');
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  const connection = getConnection();
  connection?.addEventListener?.('change', onConnectionChange);

  const intervalId = window.setInterval(() => {
    scheduleEmitWithProbes('interval');
  }, sampleEveryMs);

  scheduleEmitWithProbes('init');

  return {
    stop: () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      connection?.removeEventListener?.('change', onConnectionChange);
      //  Note: shared longTaskBuffer is NOT disconnected here — it's owned by main.ts
    },
    runNow: async () => {
      await emitWithProbes('manual');
    },
  };
}
